import { supabase } from '@/integrations/supabase/client';
import { DebugLogger } from '@/utils/debugLogger';
import { captureError, captureMessage } from '@/lib/sentry';
import type { UserRole } from './authContext';

/**
 * Fetches user role from database with proper error handling and Sentry logging.
 * If no role exists, creates a default 'apprenant_particulier' role.
 */
export async function fetchUserRole(userId: string): Promise<UserRole | null> {
  try {
    DebugLogger.log('auth', 'Fetching user role', { userId });
    
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .order('role');

    if (error) {
      captureError(new Error(`RLS Error fetching user role: ${error.message}`), {
        context: 'auth-role-fetch-failed',
        userId,
        errorCode: error.code,
        errorDetails: error.details,
      });
      DebugLogger.log('error', 'RLS Error fetching user role', error);
      return 'apprenant_particulier';
    }
    
    // If no role found, create one
    if (!data || data.length === 0) {
      DebugLogger.log('auth', 'No role found, creating default role', { userId });
      captureMessage(`No role found for user ${userId}, creating default`, 'info');
      
      const { error: insertError } = await supabase
        .from('user_roles')
        .insert({ user_id: userId, role: 'apprenant_particulier' });
      
      if (insertError) {
        captureError(new Error(`Error creating default role: ${insertError.message}`), {
          context: 'auth-role-creation-failed',
          userId,
        });
        DebugLogger.log('error', 'Error creating default role', insertError);
      }
      return 'apprenant_particulier';
    }
    
    // If user has multiple roles, prioritize admin roles
    const roles = data.map(r => r.role);
    if (roles.includes('admin_nocodeo')) return 'admin_nocodeo';
    if (roles.includes('admin_entreprise')) return 'admin_entreprise';
    if (roles.includes('formateur')) return 'formateur';
    if (roles.includes('apprenant_particulier')) return 'apprenant_particulier';
    
    return roles[0] as UserRole || 'apprenant_particulier';
  } catch (error) {
    captureError(error as Error, {
      context: 'auth-role-fetch-exception',
      userId,
    });
    console.error('Error fetching user role:', error);
    return 'apprenant_particulier';
  }
}

/**
 * Fetches user profile from database.
 * Returns null on error (non-blocking).
 */
export async function fetchUserProfile(userId: string): Promise<{ first_name?: string; last_name?: string } | null> {
  try {
    DebugLogger.log('auth', 'Fetching user profile', { userId });
    
    const { data, error } = await supabase
      .from('profiles')
      .select('first_name, last_name')
      .eq('user_id', userId)
      .single();

    if (error) {
      DebugLogger.log('error', 'Error fetching user profile', error);
      return null;
    }

    return data;
  } catch (error) {
    DebugLogger.log('error', 'Exception fetching user profile', error);
    return null;
  }
}
